// Wave.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
const TITLE = "Kalkulator Gelombang - Hitung Panjang Gelombang, Frekuensi, atau Kecepatan Gelombang";
const DESCRIPTION =
  "Kalkulator gelombang komprehensif: Hitung panjang gelombang, frekuensi, atau kecepatan gelombang menggunakan nilai input. Alat yang akurat dan mudah digunakan.";
const KEYWORDS =
  "gelombang, menghitung panjang gelombang, menghitung frekuensi, menghitung kecepatan gelombang, kalkulator gelombang";
const OG_URL = "https://do-calculate.com/calculator/id/wave";
function WaveCalculator() {
  const [wavelength, setWavelength] = useState("");
  const [frequency, setFrequency] = useState("");
  const [waveSpeed, setWaveSpeed] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculateWavelength");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const wavelengthValue = parseFloat(wavelength) || 0;
    const frequencyValue = parseFloat(frequency) || 0;
    const waveSpeedValue = parseFloat(waveSpeed) || 0;
    switch (calculationMode) {
      case "calculateWavelength":
        if (waveSpeedValue > 0 && frequencyValue > 0) {
          const calculatedWavelength = waveSpeedValue / frequencyValue;
          setWavelength(Math.round(calculatedWavelength * 100) / 100);
        } else {
          setError("Harap masukkan kecepatan gelombang dan frekuensi dengan benar");
        }
        break;
      case "calculateFrequency":
        if (waveSpeedValue > 0 && wavelengthValue > 0) {
          const calculatedFrequency = waveSpeedValue / wavelengthValue;
          setFrequency(Math.round(calculatedFrequency * 100) / 100);
        } else {
          setError("Harap masukkan kecepatan gelombang dan panjang gelombang dengan benar");
        }
        break;
      case "calculateWaveSpeed":
        if (wavelengthValue > 0 && frequencyValue > 0) {
          const calculatedWaveSpeed = wavelengthValue * frequencyValue;
          setWaveSpeed(Math.round(calculatedWaveSpeed * 100) / 100);
        } else {
          setError("Harap masukkan panjang gelombang dan frekuensi dengan benar");
        }
        break;
      default:
        break;
    }
  }, [wavelength, frequency, waveSpeed, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleWavelengthChange = (e) => setWavelength(e.target.value);
  const handleFrequencyChange = (e) => setFrequency(e.target.value);
  const handleWaveSpeedChange = (e) => setWaveSpeed(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateWavelength":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Panjang Gelombang:</h4>
            <ol>
              <li>Masukkan kecepatan gelombang (dalam meter/detik) pada kolom yang disediakan.</li>
              <li>Masukkan frekuensi (dalam Hertz) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan panjang gelombang.</li>
              <li>Panjang gelombang sama dengan kecepatan gelombang / frekuensi.</li>
            </ol>
          </div>
        );
      case "calculateFrequency":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Frekuensi:</h4>
            <ol>
              <li>Masukkan kecepatan gelombang (dalam meter/detik) pada kolom yang disediakan.</li>
              <li>Masukkan panjang gelombang (dalam meter) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan frekuensi.</li>
              <li>Frekuensi sama dengan kecepatan gelombang / panjang gelombang.</li>
            </ol>
          </div>
        );
      case "calculateWaveSpeed":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Kecepatan Gelombang:</h4>
            <ol>
              <li>Masukkan panjang gelombang (dalam meter) pada kolom yang disediakan.</li>
              <li>Masukkan frekuensi (dalam Hertz) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan kecepatan gelombang.</li>
              <li>Kecepatan gelombang sama dengan panjang gelombang × frekuensi.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator gelombang ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Panjang Gelombang dengan Kecepatan Gelombang dan Frekuensi yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui kecepatan gelombang (dalam meter/detik) dan frekuensi (dalam Hertz),
                  Anda dapat menghitung panjang gelombang (dalam meter) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Panjang Gelombang = Kecepatan Gelombang / Frekuensi</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Frekuensi dengan Kecepatan Gelombang dan Panjang Gelombang yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui kecepatan gelombang (dalam meter/detik) dan panjang gelombang (dalam
                  meter), Anda dapat menghitung frekuensi (dalam Hertz) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Frekuensi = Kecepatan Gelombang / Panjang Gelombang</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Kecepatan Gelombang dengan Panjang Gelombang dan Frekuensi yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui panjang gelombang (dalam meter) dan frekuensi (dalam Hertz), Anda dapat
                  menghitung kecepatan gelombang (dalam meter/detik) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Kecepatan Gelombang = Panjang Gelombang × Frekuensi</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan terkait gelombang.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div className="d-flex flex-row mb-2">
                      <div>
                        <input
                          type="radio"
                          id="calculateWavelength"
                          name="calculationMode"
                          value="calculateWavelength"
                          checked={calculationMode === "calculateWavelength"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateWavelength" className="ms-2">
                          Hitung Panjang Gelombang
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateFrequency"
                          name="calculationMode"
                          value="calculateFrequency"
                          checked={calculationMode === "calculateFrequency"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateFrequency" className="ms-2">
                          Hitung Frekuensi
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateWaveSpeed"
                          name="calculationMode"
                          value="calculateWaveSpeed"
                          checked={calculationMode === "calculateWaveSpeed"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateWaveSpeed" className="ms-2">
                          Hitung Kecepatan Gelombang
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculateWavelength" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="waveSpeedInput"
                        >
                          Kecepatan Gelombang (meter/detik)
                        </label>
                      </b>
                      <input
                        id="waveSpeedInput"
                        type="number"
                        placeholder="300"
                        value={waveSpeed}
                        onChange={handleWaveSpeedChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateFrequency" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="wavelengthInput"
                        >
                          Panjang Gelombang (meter)
                        </label>
                      </b>
                      <input
                        id="wavelengthInput"
                        type="number"
                        placeholder="0.5"
                        value={wavelength}
                        onChange={handleWavelengthChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateWaveSpeed" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="frequencyInput"
                        >
                          Frekuensi (Hertz)
                        </label>
                      </b>
                      <input
                        id="frequencyInput"
                        type="number"
                        placeholder="600"
                        value={frequency}
                        onChange={handleFrequencyChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculateWavelength" &&
                          wavelength !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Panjang Gelombang (meter): {wavelength} meter
                            </p>
                          )}
                        {calculationMode === "calculateFrequency" &&
                          frequency !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Frekuensi (Hertz): {frequency} Hertz
                            </p>
                          )}
                        {calculationMode === "calculateWaveSpeed" &&
                          waveSpeed !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Kecepatan Gelombang (meter/detik): {waveSpeed} meter/detik
                            </p>
                          )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr /><div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default WaveCalculator;